import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Edit } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CreatorEditDialog } from "@/components/providers/CreatorEditDialog";

export default function Creators() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data: creatorsList, isLoading } = useQuery({
    queryKey: ["creators-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("*, royalty_agency(agency_name)")
        .order("surname");

      if (error) {
        console.error("Error fetching creators:", error);
        throw error;
      }

      console.log("Fetched creators:", data);
      return data;
    },
  });

  const handleEditCreator = (creator) => {
    console.log("Opening edit dialog for creator:", creator);
    setSelectedCreator(creator);
    setIsEditDialogOpen(true);
  };

  const filteredCreators = creatorsList?.filter((creator) => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      creator.name.toLowerCase().includes(searchTerm) ||
      creator.surname.toLowerCase().includes(searchTerm) ||
      (creator.nick_name &&
        creator.nick_name.toLowerCase().includes(searchTerm))
    );
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Creators</h1>
        </div>

        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search creators..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>

        {isLoading ? (
          <div>Loading creators...</div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCreators?.map((creator) => (
                  <TableRow key={creator.creator_id}>
                    <TableCell className="font-medium">
                      {creator.name} {creator.surname}
                      {creator.nick_name && (
                        <span className="text-muted-foreground ml-2">
                          ({creator.nick_name})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{creator.email}</TableCell>
                    <TableCell>{creator.phone_number}</TableCell>
                    <TableCell>
                      {creator.shop ? (
                        <a
                          href={creator.shop}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          Shop Link
                        </a>
                      ) : (
                        "No shop"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={creator.verified ? "default" : "secondary"}>
                        {creator.verified ? "Verified" : "Unverified"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {creator.royalty_agency?.agency_name || "None"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditCreator(creator)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <CreatorEditDialog
          creator={selectedCreator}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
        />
      </div>
    </AdminLayout>
  );
}